import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useSegmentStore } from '@/src/segmentation/segments';
import { boundScalars } from '@/src/segmentation/masks/overlap';
import { emptyExtent, extentContainsIndex, growExtent, maskOffset } from '@/src/segmentation/geometry';

export type SliceFill = {
  axis: number; slice: number; u: number; v: number; width: number;
  indices: Uint32Array; source?: string; target: string;
};

/** Four-connected equal-label region on one plane; diagonals are separate. */
export function connectedRegion(labels: Uint16Array, width: number, height: number, x: number, y: number) {
  if (![width,height,x,y].every(Number.isInteger) || width * height !== labels.length ||
      x < 0 || y < 0 || x >= width || y >= height) throw new Error('Invalid fill plane or seed.');
  const seed = x + width * y; const value = labels[seed];
  const queue = new Uint32Array(labels.length); const visited = new Uint8Array(labels.length);
  let count = 1; queue[0] = seed; visited[seed] = 1;
  const add = (index: number) => {
    if (!visited[index] && labels[index] === value) { visited[index] = 1; queue[count++] = index; }
  };
  for (let head = 0; head < count; head++) {
    const index = queue[head]; const cx = index % width; const cy = Math.floor(index / width);
    if (cx) add(index - 1); if (cx + 1 < width) add(index + 1);
    if (cy) add(index - width); if (cy + 1 < height) add(index + width);
  }
  return queue.slice(0, count);
}

const voxel = (fill: SliceFill, index: number) => {
  const ijk: [number, number, number] = [0,0,0];
  ijk[fill.axis] = fill.slice; ijk[fill.u] = index % fill.width; ijk[fill.v] = Math.floor(index / fill.width);
  return ijk;
};

export function planSliceFill(imageId: string, axis: number, ijk: [number,number,number],
                              rows: { segmentId: string; labelValue: number }[], target: string): SliceFill | undefined {
  const image = useImageCacheStore().getVtkImageData(imageId);
  if (!image || ![0,1,2].includes(axis)) throw new Error('A source image and slice axis are required.');
  const dims = image.getDimensions();
  if (ijk.some((n,i) => !Number.isInteger(n) || n < 0 || n >= dims[i])) throw new Error('Fill seed lies outside the source.');
  const axes = [0,1,2].filter(n => n !== axis); const [u,v] = axes;
  const width = dims[u]; const height = dims[v]; const plane = new Uint16Array(width * height);
  const segmentation = useSegmentationStore(); const registry = useSegmentStore().segments;
  for (const row of rows) {
    const mask = boundScalars(segmentation.maskFor(imageId,row.segmentId)?.representations.labelmap);
    if (!mask || ijk[axis] < mask.extent[2*axis] || ijk[axis] > mask.extent[2*axis+1]) continue;
    const point: [number,number,number] = [...ijk];
    for (let y = Math.max(0,mask.extent[2*v]); y <= Math.min(height-1,mask.extent[2*v+1]); y++) {
      point[v] = y;
      for (let x = Math.max(0,mask.extent[2*u]); x <= Math.min(width-1,mask.extent[2*u+1]); x++) {
        point[u] = x;
        if (!mask.scalars[maskOffset(mask,...point)]) continue;
        const offset = x + y * width;
        if (plane[offset] && plane[offset] !== row.labelValue) throw new Error('Resolve overlapping labels before using the fill bucket.');
        plane[offset] = row.labelValue;
      }
    }
  }
  const value = plane[ijk[u] + ijk[v] * width];
  const targetRow = rows.find(row => row.segmentId === target);
  if (!targetRow || registry.getSegment(target)?.locked) throw new Error('Select an unlocked target label.');
  if (value === targetRow.labelValue) return;
  const source = rows.find(row => row.labelValue === value)?.segmentId;
  if (source && registry.getSegment(source)?.locked) throw new Error('The connected source label is locked.');
  return { axis, slice: ijk[axis], u, v, width,
           indices: connectedRegion(plane,width,height,ijk[u],ijk[v]), source, target };
}

/** Apply or undo one compact region, modifying neither adjacent slices nor CT. */
export function applySliceFill(imageId: string, fill: SliceFill, reverse = false) {
  const segmentation = useSegmentationStore(); const registry = useSegmentStore().segments;
  const ids = [fill.source, fill.target].filter((id): id is string => !!id);
  if (ids.some(id => !registry.getSegment(id) || registry.getSegment(id)?.locked)) throw new Error('Unlock affected labels before fill/undo/redo.');
  const extent = emptyExtent();
  fill.indices.forEach(index => growExtent(extent,...voxel(fill,index)));
  const writeTo = reverse ? fill.source : fill.target;
  // Complete allocation/growth before clearing anything from the source.
  if (writeTo) {
    const mask = segmentation.maskFor(imageId,writeTo) ?? segmentation.createMask(segmentation.ensureSegmentationForImage(imageId).id,writeTo);
    const access = segmentation.maskVoxels(mask.id); access.materialize(); access.ensureContains(extent);
  }
  for (const segmentId of ids) {
    let mask = segmentation.maskFor(imageId,segmentId);
    if (!mask && segmentId !== writeTo) continue;
    mask ??= segmentation.createMask(segmentation.ensureSegmentationForImage(imageId).id,segmentId);
    const access = segmentation.maskVoxels(mask.id); access.materialize();
    const bounded = boundScalars(access.binding());
    if (!bounded) continue;
    for (const index of fill.indices) {
      const point = voxel(fill,index);
      if (extentContainsIndex(bounded.extent,...point)) bounded.scalars[maskOffset(bounded,...point)] = segmentId === writeTo ? 1 : 0;
    }
    access.image().modified();
  }
}
